import { useId, type ReactNode, type Ref } from 'react'
import { SectionHeadline } from '@/components/SectionHeadline'
import { PageSection } from '@/features/jobs/details/components/PageLayout'
import {
  CardListItem,
  CardItemContent,
  CardItemIcon,
  CardSubtitle,
  CardTitle,
  CardList,
} from '@/components/Card'
import { LabelChip } from '@/components/Chip'
import { match } from '@/utils/pattern-match-util'
import { AutoSkeleton, useSkeletonListContext } from '@/components/Skeleton'
import { useIsSpacious } from '@/hooks/viewport-hooks'

export interface ChecklistSectionProps {
  children?: ReactNode
  testId?: string
}

/**
 * Personal info section.
 */
export function PersonalInfoSection({
  children,
  testId,
}: ChecklistSectionProps) {
  const headlineId = useId()
  return (
    <PageSection testId={testId}>
      <SectionHeadline id={headlineId}>
        Your personal information
      </SectionHeadline>
      <CardList aria-labelledby={headlineId}>{children}</CardList>
    </PageSection>
  )
}

/**
 * Business info section.
 */
export function BusinessInfoSection({
  children,
  testId,
}: ChecklistSectionProps) {
  const headlineId = useId()
  return (
    <PageSection testId={testId}>
      <SectionHeadline id={headlineId}>
        Your business information
      </SectionHeadline>
      <CardList aria-labelledby={headlineId}>{children}</CardList>
    </PageSection>
  )
}

/**
 * Notes section.
 */
export function NotesSection({ children, testId }: ChecklistSectionProps) {
  const headlineId = useId()
  return (
    <PageSection testId={testId}>
      <SectionHeadline id={headlineId}>Additional information</SectionHeadline>
      <CardList aria-labelledby={headlineId}>{children}</CardList>
    </PageSection>
  )
}

/**
 * Missing documents section.
 * Children should be {@link EmptyChecklistDocumentCardListItem}s.
 */
export function MissingChecklistDocumentsSection({
  children,
  testId,
}: ChecklistSectionProps) {
  const headlineId = useId()
  return (
    <PageSection testId={testId}>
      <SectionHeadline id={headlineId}>Pending documents</SectionHeadline>
      <CardList aria-labelledby={headlineId}>{children}</CardList>
    </PageSection>
  )
}

interface PersonalInfoCardListItemProps {
  children?: ReactNode
  onAction?: () => unknown
  fullName: string
  testId?: string
}

/**
 * Personal Info Card List Item
 */
export function PersonalInfoCardListItem({
  fullName,
  onAction,
  testId,
}: PersonalInfoCardListItemProps) {
  const isSpacious = useIsSpacious()
  return (
    <CardListItem onAction={onAction} textValue={fullName} testId={testId}>
      <CardItemContent
        icon={isSpacious && <CardItemIcon name="user" />}
        isInteractive={Boolean(onAction)}
        extras={<LabelChip text="Done" variant="default" />}
      >
        <CardTitle>{fullName}</CardTitle>
        <CardSubtitle variant="sm">You</CardSubtitle>
      </CardItemContent>
    </CardListItem>
  )
}

interface EmptyInfoCardListItemProps {
  children?: ReactNode
  onAction?: () => unknown
  variant?: 'default' | 'simple'
  testId?: string
}

/**
 * Empty Personal Info Card Button
 */
export function EmptyPersonalInfoCardListItem({
  onAction,
  variant = 'default',
  testId,
}: EmptyInfoCardListItemProps) {
  const textValue = 'Personal details'
  const isSpacious = useIsSpacious()
  return (
    <CardListItem onAction={onAction} textValue={textValue} testId={testId}>
      <CardItemContent
        icon={isSpacious && <CardItemIcon name="user" />}
        isInteractive={Boolean(onAction)}
        extras={
          variant === 'default' && <LabelChip text="Missing" variant="danger" />
        }
      >
        <CardTitle>{textValue}</CardTitle>
        <CardSubtitle variant="sm">
          Required to set up your tax filing.
        </CardSubtitle>
      </CardItemContent>
    </CardListItem>
  )
}

interface SpousalInfoCardListItemProps {
  children?: ReactNode
  onAction?: () => unknown
  fullName: string
  testId?: string
}

/**
 * Spousal Info Card List Item
 */
export function SpousalInfoCardListItem({
  fullName,
  onAction,
  testId,
}: SpousalInfoCardListItemProps) {
  const isSpacious = useIsSpacious()
  return (
    <CardListItem onAction={onAction} textValue={fullName} testId={testId}>
      <CardItemContent
        icon={isSpacious && <CardItemIcon name="spouse" />}
        isInteractive={Boolean(onAction)}
        extras={<LabelChip text="Done" variant="default" />}
      >
        <CardTitle>{fullName}</CardTitle>
        <CardSubtitle variant="sm">Your spouse</CardSubtitle>
      </CardItemContent>
    </CardListItem>
  )
}

/**
 * Empty Spousal Info Card List Item
 */
export function EmptySpousalInfoCardListItem({
  onAction,
  variant = 'default',
  testId,
}: EmptyInfoCardListItemProps) {
  const textValue = "Spouse's details"
  const isSpacious = useIsSpacious()
  return (
    <CardListItem onAction={onAction} textValue={textValue} testId={testId}>
      <CardItemContent
        icon={isSpacious && <CardItemIcon name="spouse" />}
        isInteractive={Boolean(onAction)}
        extras={
          variant === 'default' && <LabelChip text="Missing" variant="danger" />
        }
      >
        <CardTitle>{textValue}</CardTitle>
        <CardSubtitle variant="sm">
          Needed for joint filing and deductions.
        </CardSubtitle>
      </CardItemContent>
    </CardListItem>
  )
}

interface DependentsInfoCardListItemProps {
  children?: ReactNode
  onAction?: () => unknown
  fullNames: string[]
  testId?: string
}

/**
 * Dependents Info Card List Item
 */
export function DependentsInfoCardListItem({
  fullNames,
  onAction,
  testId,
}: DependentsInfoCardListItemProps) {
  const fullNamesText = match(fullNames.length, {
    [1]: () => fullNames[0],
    [2]: () => `${fullNames[0]} and ${fullNames[1]}`,
    [match.DEFAULT]: () => `${fullNames.length || 'No'} dependents`,
  })
  const isSpacious = useIsSpacious()
  return (
    <CardListItem onAction={onAction} textValue={fullNamesText} testId={testId}>
      <CardItemContent
        icon={isSpacious && <CardItemIcon name="dependent" />}
        isInteractive={Boolean(onAction)}
        extras={<LabelChip text="Done" variant="default" />}
      >
        <CardTitle>{fullNamesText}</CardTitle>
        <CardSubtitle variant="sm">Your dependents</CardSubtitle>
      </CardItemContent>
    </CardListItem>
  )
}

/**
 * Empty Dependents Info Card List Item
 */
export function EmptyDependentsInfoCardListItem({
  onAction,
  variant = 'default',
  testId,
}: EmptyInfoCardListItemProps) {
  const textValue = 'Dependents details'
  const isSpacious = useIsSpacious()
  return (
    <CardListItem onAction={onAction} textValue={textValue} testId={testId}>
      <CardItemContent
        icon={isSpacious && <CardItemIcon name="dependent" />}
        isInteractive={Boolean(onAction)}
        extras={
          variant === 'default' && <LabelChip text="Missing" variant="danger" />
        }
      >
        <CardTitle>{textValue}</CardTitle>
        <CardSubtitle variant="sm">
          Ensure you receive all eligible tax credits.
        </CardSubtitle>
      </CardItemContent>
    </CardListItem>
  )
}

interface BusinessInfoCardListItemProps {
  children?: ReactNode
  onAction?: () => unknown
  businessName: string
  testId?: string
}

/**
 * Business Info Card List Item
 */
export function BusinessInfoCardListItem({
  businessName,
  onAction,
  testId,
}: BusinessInfoCardListItemProps) {
  const isSpacious = useIsSpacious()
  return (
    <CardListItem onAction={onAction} textValue={businessName} testId={testId}>
      <CardItemContent
        icon={isSpacious && <CardItemIcon name="business" />}
        isInteractive={Boolean(onAction)}
        extras={<LabelChip text="Done" variant="default" />}
      >
        <CardTitle>{businessName}</CardTitle>
        <CardSubtitle variant="sm">Your business</CardSubtitle>
      </CardItemContent>
    </CardListItem>
  )
}

/**
 * Empty Business Info Card List Item
 */
export function EmptyBusinessInfoCardListItem({
  onAction,
  variant = 'default',
  testId,
}: EmptyInfoCardListItemProps) {
  const textValue = 'Business details'
  const isSpacious = useIsSpacious()
  return (
    <CardListItem onAction={onAction} textValue={textValue} testId={testId}>
      <CardItemContent
        icon={isSpacious && <CardItemIcon name="business" />}
        isInteractive={Boolean(onAction)}
        extras={
          variant === 'default' && <LabelChip text="Missing" variant="danger" />
        }
      >
        <CardTitle>{textValue}</CardTitle>
        <CardSubtitle variant="sm">
          Needed to set up tax filing for your business.
        </CardSubtitle>
      </CardItemContent>
    </CardListItem>
  )
}

interface TaxRefundMethodCardListItemProps {
  children?: ReactNode
  onAction?: () => unknown
  methodTitle: string
  testId?: string
}

/**
 * Tax Refund Method Card List Item
 */
export function TaxRefundMethodCardListItem({
  methodTitle,
  onAction,
  testId,
}: TaxRefundMethodCardListItemProps) {
  const isSpacious = useIsSpacious()
  return (
    <CardListItem onAction={onAction} textValue={methodTitle} testId={testId}>
      <CardItemContent
        icon={isSpacious && <CardItemIcon name="card" />}
        isInteractive={Boolean(onAction)}
        extras={<LabelChip text="Done" variant="default" />}
      >
        <CardTitle>{methodTitle}</CardTitle>
        <CardSubtitle variant="sm">Refund method</CardSubtitle>
      </CardItemContent>
    </CardListItem>
  )
}

interface EmptyTaxRefundMethodCardListItemProps
  extends EmptyInfoCardListItemProps {
  kind: 'personal' | 'business'
}

/**
 * Empty Tax Refund Method Card List Item
 */
export function EmptyTaxRefundMethodCardListItem({
  onAction,
  variant = 'default',
  kind,
  testId,
}: EmptyTaxRefundMethodCardListItemProps) {
  const textValue =
    kind === 'personal'
      ? 'Personal tax refund method'
      : 'Business tax refund method'
  const isSpacious = useIsSpacious()
  return (
    <CardListItem onAction={onAction} textValue={textValue} testId={testId}>
      <CardItemContent
        icon={isSpacious && <CardItemIcon name="card" />}
        isInteractive={Boolean(onAction)}
        extras={
          variant === 'default' && <LabelChip text="Missing" variant="danger" />
        }
      >
        <CardTitle>{textValue}</CardTitle>
        <CardSubtitle variant="sm">
          Necessary to receive your tax refund.
        </CardSubtitle>
      </CardItemContent>
    </CardListItem>
  )
}

interface EmptyChecklistDocumentCardListItemProps {
  onAction?: () => unknown
  title: string
  description: string
  /**
   * When using `standout`, the icon will use a more attention-calling style.
   */
  variant?: 'simple' | 'standout'
  /**
   * Test ID attribute.
   */
  testId?: string
}

/**
 * Empty Tax Refund Method Card List Item
 */
export function EmptyChecklistDocumentCardListItem({
  onAction,
  title,
  description,
  variant = 'simple',
  testId,
}: EmptyChecklistDocumentCardListItemProps) {
  const textValue = title
  const isSpacious = useIsSpacious()
  return (
    <CardListItem onAction={onAction} textValue={textValue} testId={testId}>
      <CardItemContent
        isInteractive={Boolean(onAction)}
        icon={
          isSpacious && (
            <CardItemIcon
              variant={
                variant === 'standout' ? 'surface-background' : 'default'
              }
              name="upload"
            />
          )
        }
      >
        <CardTitle>{textValue}</CardTitle>
        <CardSubtitle variant="sm">{description}</CardSubtitle>
      </CardItemContent>
    </CardListItem>
  )
}

interface NotesCardListItemProps {
  onAction?: () => unknown
  text: string
  testId?: string
}

/**
 * Notes Card List Item
 */
export function NotesCardListItem({
  text,
  onAction,
  testId,
}: NotesCardListItemProps) {
  const isSpacious = useIsSpacious()
  const textValue = text || 'Notes'

  return (
    <CardListItem onAction={onAction} textValue={textValue} testId={testId}>
      <CardItemContent
        icon={isSpacious && <CardItemIcon name="edit" />}
        isInteractive={Boolean(onAction)}
        extras={<LabelChip text="Done" variant="default" />}
      >
        <CardTitle>{textValue}</CardTitle>
        <CardSubtitle variant="sm">Additional information</CardSubtitle>
      </CardItemContent>
    </CardListItem>
  )
}

interface EmptyNotesCardListItemProps {
  onAction?: () => unknown
  variant?: 'default' | 'simple'
  testId?: string
}

/**
 * Empty Notes Card List Item
 */
export function EmptyNotesCardListItem({
  onAction,
  variant = 'default',
  testId,
}: EmptyNotesCardListItemProps) {
  const textValue = 'Additional notes'
  const isSpacious = useIsSpacious()

  return (
    <CardListItem onAction={onAction} textValue={textValue} testId={testId}>
      <CardItemContent
        icon={isSpacious && <CardItemIcon name="edit" />}
        isInteractive={Boolean(onAction)}
        extras={
          variant === 'default' && <LabelChip text="Missing" variant="danger" />
        }
      >
        <CardTitle>{textValue}</CardTitle>
        <CardSubtitle variant="sm">Add instructions for your Pro</CardSubtitle>
      </CardItemContent>
    </CardListItem>
  )
}

/**
 * Personal Info Card List Item
 */
export function SkeletonChecklistItemCardListItem() {
  const { vary, ref } = useSkeletonListContext()
  return (
    <CardListItem textValue="Loading" ref={ref as Ref<HTMLDivElement>}>
      <CardItemContent
        icon={<CardItemIcon skeleton name="user" />}
        isInteractive
        isSkeleton
      >
        <CardTitle>
          <AutoSkeleton isSkeleton characters={vary(40)} />
        </CardTitle>
      </CardItemContent>
    </CardListItem>
  )
}
